import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface QuizRegenerationTarget {
  moduleId: string;
  courseId: string;
}

export interface QuizRegenerationResult {
  moduleId: string;
  courseId: string;
  success: boolean;
  error?: string;
  firstQuestion?: string;
}

export interface BulkRegenerationSummary {
  total: number;
  succeeded: number;
  failed: number;
  results: QuizRegenerationResult[];
}

/**
 * Known modules with wrong-topic quizzes that need regeneration
 * Note: power-automate-enterprise/enterprise-architecture excluded - missing content
 */
export const WRONG_TOPIC_QUIZ_MODULES: QuizRegenerationTarget[] = [
  { moduleId: 'angular-architecture-patterns', courseId: 'angular-advanced' },
  { moduleId: 'vue-enterprise-patterns', courseId: 'vuejs-advanced' },
  { moduleId: 'rust-database', courseId: 'rust-web-development' },
  // { moduleId: 'enterprise-architecture', courseId: 'power-automate-enterprise' }, // Missing content - needs generation first
  { moduleId: 'performance-optimization', courseId: 'power-automate-enterprise' },
  { moduleId: 'enterprise-architecture', courseId: 'rpa-architecture-governance' },
  { moduleId: 'module4', courseId: 'n8n-workflow-automation' },
  { moduleId: 'react-fund-2', courseId: 'react-fundamentals' }
];

/**
 * Regenerate a single module's quiz
 */
export async function regenerateQuiz(
  moduleId: string, 
  courseId: string
): Promise<QuizRegenerationResult> {
  try {
    logger.info(`Regenerating quiz for ${moduleId} in ${courseId}`);

    const { data, error } = await supabase.functions.invoke('regenerate-quiz', {
      body: { moduleId, courseId, forceRegenerate: true }
    });

    if (error) {
      throw error;
    }

    return {
      moduleId,
      courseId,
      success: data?.success || false,
      firstQuestion: data?.firstQuestion,
      error: data?.error
    };
  } catch (error: any) {
    logger.error(`Failed to regenerate quiz for ${moduleId}:`, error);
    return {
      moduleId,
      courseId,
      success: false,
      error: error.message
    };
  }
}

/**
 * Regenerate quizzes for all known wrong-topic modules
 */
export async function regenerateAllWrongTopicQuizzes(): Promise<BulkRegenerationSummary> {
  try {
    logger.info('Starting bulk quiz regeneration for wrong-topic modules');

    const { data, error } = await supabase.functions.invoke('bulk-regenerate-quizzes', {
      body: { targets: WRONG_TOPIC_QUIZ_MODULES }
    });

    if (error) {
      throw error;
    }

    return {
      total: data?.summary?.total || WRONG_TOPIC_QUIZ_MODULES.length,
      succeeded: data?.summary?.succeeded || 0,
      failed: data?.summary?.failed || 0,
      results: data?.results || []
    };
  } catch (error: any) {
    logger.error('Bulk quiz regeneration failed:', error);
    return {
      total: WRONG_TOPIC_QUIZ_MODULES.length,
      succeeded: 0,
      failed: WRONG_TOPIC_QUIZ_MODULES.length,
      results: WRONG_TOPIC_QUIZ_MODULES.map(t => ({
        moduleId: t.moduleId,
        courseId: t.courseId,
        success: false,
        error: error.message
      }))
    };
  }
}

/**
 * Check if a quiz has potentially wrong-topic questions
 */
export async function validateQuizRelevance(
  moduleId: string, 
  courseId: string
): Promise<{ isRelevant: boolean; issues: string[] }> {
  try {
    // Get the quiz
    const { data: quiz, error: quizError } = await supabase
      .from('module_quizzes')
      .select('questions')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .single();

    if (quizError || !quiz?.questions) {
      return { isRelevant: false, issues: ['Quiz not found'] };
    }

    // Get module content for comparison
    const { data: content } = await supabase
      .from('module_content')
      .select('content')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .single();

    const issues: string[] = [];
    const questions = quiz.questions as Array<{ question: string }>;
    const moduleContent = (content?.content || '').toLowerCase();

    // Check for known irrelevant patterns
    const irrelevantPatterns = [
      { pattern: 'capital of france', issue: 'Geography trivia question detected' },
      { pattern: 'red planet', issue: 'Astronomy trivia question detected' },
      { pattern: 'python module', issue: 'Python questions in non-Python module' },
      { pattern: 'undefined value in javascript', issue: 'Generic JavaScript undefined question' }
    ];

    for (const q of questions) {
      const questionLower = q.question?.toLowerCase() || '';
      
      for (const { pattern, issue } of irrelevantPatterns) {
        if (questionLower.includes(pattern) && !moduleContent.includes(pattern.split(' ')[0])) {
          issues.push(`Question "${q.question.substring(0, 50)}..." - ${issue}`);
        }
      }
    }

    return {
      isRelevant: issues.length === 0,
      issues
    };
  } catch (error: any) {
    logger.error(`Quiz validation failed for ${moduleId}:`, error);
    return { isRelevant: false, issues: [`Validation error: ${error.message}`] };
  }
}
